import type { ResultValue } from './api';

export interface LineSeries {
  name: string;
  /** Metric per x-category, aligned to the shared category axis (null = missing). */
  data: (number | null)[];
}

function toNumber(v: ResultValue): number {
  return typeof v === 'number' ? v : Number(v) || 0;
}

/**
 * Pivot long `(time, category, value)` rows into a shared time axis plus one series
 * per category value — the shape a multi-series line chart needs. Categories are
 * sorted (ISO date buckets sort chronologically); missing `(time, category)` cells
 * are `null` so `connectNulls` bridges gaps rather than dropping to zero. Series
 * keep first-seen order, matching how the palette is assigned.
 */
export function pivotToSeries(
  rows: Record<string, ResultValue>[],
  timeKey: string,
  seriesKey: string,
  metricKey: string,
): { categories: string[]; series: LineSeries[] } {
  const categories = [...new Set(rows.map((r) => String(r[timeKey] ?? '')))].sort();
  const index = new Map(categories.map((c, i) => [c, i]));
  const byName = new Map<string, (number | null)[]>();
  for (const r of rows) {
    const name = String(r[seriesKey] ?? '');
    const i = index.get(String(r[timeKey] ?? ''));
    if (i === undefined) continue;
    const data = byName.get(name) ?? categories.map(() => null);
    data[i] = toNumber(r[metricKey]);
    byName.set(name, data);
  }
  return { categories, series: [...byName.entries()].map(([name, data]) => ({ name, data })) };
}
